import { z } from 'zod';
import { Uuid } from './common';

/**
 * An order's money split into the P&L rows (centavos), frozen at close/cancel/return. Cost rows
 * are real (jobs at real grams and hours, stock units at average cost); units not produced yet
 * count at their estimate while the order is open.
 */
export const OrderBreakdown = z
  .object({
    revenueCentavos: z.number().int().describe('Items total, less the refund for ended orders.'),
    productionCentavos: z.number().int().describe('Material, machine and failures.'),
    laborCentavos: z.number().int(),
    packagingCentavos: z.number().int().describe('Packaging kit and supplies.'),
    designCentavos: z.number().int(),
    feesCentavos: z.number().int().describe('Commission, payment fee and fixed fee.'),
    taxCentavos: z.number().int().describe('Per-sale tax (percent mode only).'),
    shippingCentavos: z.number().int().describe('Shipping paid by the seller.'),
    discountsCentavos: z.number().int(),
    contributionCentavos: z
      .number()
      .int()
      .describe('Revenue minus every row above (before fixed costs).'),
    depreciationCentavos: z
      .number()
      .int()
      .describe('Printer depreciation inside production (hours × price ÷ lifetime).'),
    printSeconds: z.number().int(),
    units: z.number().int(),
    items: z.array(
      z.object({
        itemId: Uuid,
        variantId: Uuid.nullable(),
        material: z.string().nullable().describe('Main material by grams.'),
        units: z.number().int(),
        revenueCentavos: z.number().int(),
        contributionCentavos: z.number().int(),
        estimatedContributionCentavos: z.number().int(),
      }),
    ),
  })
  .meta({ id: 'OrderBreakdown' });
export type OrderBreakdown = z.infer<typeof OrderBreakdown>;
export type OrderBreakdownJson = OrderBreakdown;

const C = z.number().int();
const Month = z.string().regex(/^\d{4}-\d{2}$/);

export const ReportMonthQuery = z.object({
  month: Month.optional().describe('`YYYY-MM` (São Paulo); defaults to the current month.'),
});

export const ReportMonths = z
  .object({
    months: z.array(Month).describe('Months with orders, expenses or quotes, newest first.'),
    current: Month,
  })
  .meta({ id: 'ReportMonths' });
export type ReportMonths = z.infer<typeof ReportMonths>;

export const FixedCostItem = z.object({
  label: z.string(),
  amountCentavos: C,
  estimate: z.boolean().describe('Pending item at its expected value (e.g. the DAS setting).'),
  source: z
    .enum(['expense', 'equipment', 'prints'])
    .describe(
      "`prints`: the month's prints outside orders (tests, own use, gifts), filament + machine.",
    ),
  id: z.string().describe('The expense or equipment id; the month (YYYY-MM) for `prints`.'),
});

export const PlRow = z.object({
  key: z.enum([
    'revenue',
    'production',
    'labor',
    'packaging',
    'design',
    'fees',
    'tax',
    'shipping',
    'discounts',
  ]),
  label: z.string(),
  amountCentavos: C.describe('Revenue positive, costs negative.'),
});

export const MonthReport = z
  .object({
    month: Month,
    partial: z.boolean().describe('Current month or open orders: numbers still move.'),
    today: z.iso.date(),
    daysLeft: z.number().int().describe('Days left in the month (0 for past months).'),
    orders: z.number().int(),
    openOrders: z.number().int(),
    units: z.number().int(),
    revenueCentavos: C,
    rows: z.array(PlRow),
    contributionCentavos: C.describe('"Margem de contribuição": revenue minus every row.'),
    fixed: z.object({ totalCentavos: C, items: z.array(FixedCostItem) }),
    netProfitCentavos: C.describe('Contribution minus fixed costs.'),
    breakEven: z.object({
      fillBps: z.number().int().describe('Contribution ÷ fixed costs, capped at 100%.'),
      reached: z.boolean(),
      reachedOn: z.iso.date().nullable(),
      perUnitCentavos: C.nullable().describe('Average contribution per unit sold.'),
      perOrderCentavos: C.nullable(),
      unitsNeeded: z.number().int().nullable().describe('Units to sell this month in total.'),
      unitsMissing: z.number().int().nullable(),
      timesFixed: z.number().nullable().describe('Contribution ÷ fixed costs when reached.'),
      lowestChannel: z
        .object({ name: z.string(), marginBps: z.number().int() })
        .nullable()
        .describe('The channel pulling the average down (two or more channels sold).'),
    }),
    cash: z.object({
      salesCentavos: C.describe('Contribution of the orders whose money lands this month.'),
      depreciationCentavos: C.describe('Printer depreciation inside those sales (not cash out).'),
      inCentavos: C,
      installments: z.array(
        z.object({ label: z.string(), dueOn: z.iso.date(), amountCentavos: C, paid: z.boolean() }),
      ),
      installmentsCentavos: C,
      fixedCentavos: C.describe('Fixed expenses of the month (equipment depreciation excluded).'),
      balanceCentavos: C,
      coverageBps: z
        .number()
        .int()
        .nullable()
        .describe('Money in ÷ installments due (null without installments).'),
      missingPayout: z
        .array(z.string())
        .describe('Channels sold without "Prazo de recebimento": counted on the sale date.'),
    }),
    schedule: z
      .array(
        z.object({
          month: Month,
          installmentsCentavos: C,
          netProfitCentavos: C.nullable().describe('Null for future months.'),
        }),
      )
      .describe('"Próximos meses": installments due next to the result.'),
  })
  .meta({ id: 'MonthReport' });
export type MonthReport = z.infer<typeof MonthReport>;

export const RankingRow = z.object({
  key: z.string(),
  name: z.string(),
  color: z.string().nullable(),
  link: z.string().nullable().describe('Web path (product or channel).'),
  units: z.number().int(),
  salesCentavos: C,
  profitCentavos: C.describe('Contribution (before fixed costs).'),
  marginBps: z.number().int().nullable(),
});
export type RankingRow = z.infer<typeof RankingRow>;

export const Rankings = z
  .object({
    month: Month,
    products: z.array(RankingRow),
    channels: z.array(RankingRow),
    materials: z.array(RankingRow),
    revenueCentavos: C,
    contributionCentavos: C,
  })
  .meta({ id: 'Rankings' });
export type Rankings = z.infer<typeof Rankings>;

export const ProductionReport = z
  .object({
    month: Month,
    gramsUsed: z.number().describe('Filament used (done and failed jobs).'),
    failureBps: z.number().int().nullable().describe('Failed hours ÷ printed hours.'),
    timeDeltaBps: z.number().int().nullable().describe('Real vs estimated time, done jobs.'),
    gramsDeltaBps: z.number().int().nullable(),
    byMonth: z.array(
      z.object({
        month: Month,
        materials: z.array(z.object({ material: z.string(), grams: z.number() })),
      }),
    ),
    failures: z.array(
      z.object({
        printerId: Uuid.nullable(),
        printer: z.string(),
        material: z.string(),
        failed: z.number().int(),
        jobs: z.number().int(),
        failureBps: z.number().int(),
      }),
    ),
    estimate: z.object({
      estSeconds: z.number().int(),
      realSeconds: z.number().int(),
      estGrams: z.number(),
      realGrams: z.number(),
    }),
    reasons: z.array(z.object({ reason: z.string(), label: z.string(), count: z.number().int() })),
    lastFailure: z
      .object({ number: z.string(), pct: z.number().int(), reason: z.string(), on: z.iso.date() })
      .nullable(),
  })
  .meta({ id: 'ProductionReport' });
export type ProductionReport = z.infer<typeof ProductionReport>;

const Funnel = z.object({
  key: z.string(),
  sent: z.number().int(),
  accepted: z.number().int(),
  declined: z.number().int(),
  expired: z.number().int(),
  open: z.number().int(),
});
export const QuotesReport = z
  .object({
    month: Month,
    firstNumber: z.string().nullable(),
    lastNumber: z.string().nullable(),
    totals: Funnel,
    conversionBps: z.number().int().nullable().describe('Accepted ÷ sent (open ones included).'),
    byChannel: z.array(Funnel),
    byBand: z.array(Funnel).describe('Até R$ 50 · R$ 50 a R$ 150 · Acima de R$ 150.'),
    expiringSoon: z
      .object({ number: z.string(), customer: z.string().nullable(), validUntil: z.iso.date() })
      .nullable(),
  })
  .meta({ id: 'QuotesReport' });
export type QuotesReport = z.infer<typeof QuotesReport>;

export const PaybackReport = z
  .object({
    investmentCentavos: C,
    equipment: z.array(z.object({ name: z.string(), valueCentavos: C })),
    recoveredCentavos: C,
    remainingCentavos: C,
    recoveredBps: z.number().int(),
    paceCentavos: C.nullable().describe('Average recovered per month (last 3 months with sales).'),
    paybackMonth: Month.nullable().describe('Projected month the investment is recovered.'),
    months: z.array(
      z.object({
        month: Month,
        recoveredCentavos: C.describe('Net profit + depreciation counted that month.'),
        cumulativeCentavos: C,
        projected: z.boolean(),
      }),
    ),
  })
  .meta({ id: 'PaybackReport' });
export type PaybackReport = z.infer<typeof PaybackReport>;

export const RevenueLimitReport = z
  .object({
    year: z.number().int(),
    revenueCentavos: C.describe('Revenue of orders sold this year (refunds deducted).'),
    limitCentavos: C,
    usedBps: z.number().int(),
    alertAtCentavos: C,
    alert: z.boolean(),
    byMonth: z.array(z.object({ month: Month, revenueCentavos: C })),
  })
  .meta({ id: 'RevenueLimitReport' });
export type RevenueLimitReport = z.infer<typeof RevenueLimitReport>;

export const ProductStatsQuery = z.object({
  months: z.coerce.number().int().min(1).max(24).default(3),
});

export const ProductStats = z
  .object({
    from: z.iso.date(),
    to: z.iso.date(),
    units: z.number().int(),
    revenueCentavos: C,
    yearShareBps: z.number().int().nullable().describe('Share of this year’s revenue.'),
    realMarginBps: z.number().int().nullable(),
    estimatedMarginBps: z.number().int().nullable(),
    plates: z.number().int(),
    failedPlates: z.number().int(),
    failureBps: z.number().int().nullable().describe('Failed plates ÷ plates printed.'),
    configuredFailureBps: z.number().int().nullable(),
    byMonth: z.array(z.object({ month: Month, units: z.number().int() })),
    variants: z.array(
      z.object({
        variantId: Uuid,
        name: z.string(),
        units: z.number().int(),
        revenueCentavos: C,
        marginBps: z.number().int().nullable(),
        estSecondsPerUnit: z.number().int().nullable(),
        realSecondsPerUnit: z.number().int().nullable(),
      }),
    ),
  })
  .meta({ id: 'ProductStats' });
export type ProductStats = z.infer<typeof ProductStats>;

export const AlertKind = z.enum([
  'backup_failed',
  'order_late',
  'filament_low',
  'supply_low',
  'quote_expiring',
  'job_finishing',
  'maintenance_due',
  'installment_due',
  'revenue_limit',
  'login_failures',
]);
export type AlertKind = z.infer<typeof AlertKind>;

export const Alert = z
  .object({
    id: z.string().describe('Stable key (kind + record + date) for "mark as read".'),
    kind: AlertKind,
    severity: z.enum(['err', 'warn', 'info']),
    title: z.string(),
    detail: z.string(),
    link: z.string().describe('Web path to act on it.'),
  })
  .meta({ id: 'Alert' });
export type Alert = z.infer<typeof Alert>;
export const AlertList = z.object({ data: z.array(Alert) }).meta({ id: 'AlertList' });

// ---- Calibração (Canais) -----------------------------------------------------------------------

export const CalibrationQuery = z.object({
  days: z.coerce.number().int().min(7).max(365).default(90),
});
export const Calibration = z
  .object({
    from: z.iso.date(),
    minOrders: z.number().int().describe('Below this, a channel reads "poucos pedidos".'),
    channels: z.array(
      z.object({
        channelId: Uuid,
        name: z.string(),
        color: z.string().nullable(),
        orders: z.number().int().describe('Orders whose real charges were typed.'),
        predictedCentavos: z
          .number()
          .int()
          .describe('Commission + fixed fee + shipping, as predicted.'),
        realCentavos: z.number().int().describe('The same, as charged.'),
        diffBps: z.number().int().nullable().describe('(real − predicted) ÷ predicted.'),
        few: z.boolean(),
      }),
    ),
  })
  .meta({ id: 'Calibration' });
export type Calibration = z.infer<typeof Calibration>;
